/**
 * Close formatting that is still open in a partially streamed answer, so "**Trave"
 * renders as bold text instead of showing raw asterisks until the closing marker
 * arrives. Only used while streaming; the final answer is rendered as sent.
 */
export function closeOpenMarkdown(text: string): string {
  // An unterminated code fence: close it and leave the rest alone.
  const fences = text.match(/^\s*(```|~~~)/gm)?.length ?? 0
  if (fences % 2 === 1) return `${text}\n\`\`\``

  // Ignore inline code spans when counting emphasis markers.
  const withoutCode = text.replace(/`[^`\n]*`/g, '')
  let suffix = ''
  if ((withoutCode.match(/`/g)?.length ?? 0) % 2 === 1) suffix = '`' + suffix

  const prose = withoutCode.replace(/`[^\n]*$/, '')
  const bold = prose.match(/\*\*/g)?.length ?? 0
  if (bold % 2 === 1) {
    // "**" with nothing after it yet would render literally; drop it instead.
    if (/\*\*\s*$/.test(text)) return text.replace(/\*\*\s*$/, '')
    suffix = '**' + suffix
  }
  return text + suffix
}

const LIST_ITEM = /^ {0,3}([-*+]|\d{1,9}[.)])\s/
const QUOTE = /^ {0,3}>/
const FENCE = /^ {0,3}(`{3,}|~{3,})/

/**
 * Split an answer into top-level blocks at blank lines, so a streaming answer can
 * re-render only its last, still-growing block. Never splits inside a code fence,
 * before an indented continuation, or between items of one list or quote, so each
 * block renders exactly as it would inside the whole document.
 */
export function splitMarkdownBlocks(text: string): string[] {
  const blocks: string[] = []
  let current: string[] = []
  let fence: string | null = null
  let afterBlank = false
  for (const line of text.split('\n')) {
    const marker = FENCE.exec(line)?.[1] ?? null
    if (fence) {
      current.push(line)
      if (marker && marker[0] === fence[0] && marker.length >= fence.length) fence = null
      continue
    }
    if (!line.trim()) {
      afterBlank = current.length > 0
      current.push(line)
      continue
    }
    if (afterBlank) {
      const first = current.find((l) => l.trim()) ?? ''
      const continues =
        /^\s/.test(line) || (LIST_ITEM.test(line) && LIST_ITEM.test(first)) || (QUOTE.test(line) && QUOTE.test(first))
      if (!continues) {
        blocks.push(current.join('\n').trimEnd())
        current = []
      }
      afterBlank = false
    }
    if (marker) fence = marker
    current.push(line)
  }
  if (current.length) blocks.push(current.join('\n'))
  return blocks
}
